import React from 'react';
import _ from 'lodash';
import { Translate, TranslateHTML } from 'core/translate';
import { normalize as cgiNormalize } from '../common/cgi/cgi';
import { computePricing } from '../common/pricing/pricing';
import MathBox from '../components/mathBox/MathBox';
import Currency from '../common/intl/Currency';
import NotificationsContext from '../common/notifications/NotificationsContext';
import { Dagger } from '../common/notifications/NotificationsTrigger';
import { Accordion } from 'core/controls';
import SelectedAccessoriesGroup from './SelectedAccessoriesGroup';
import StandardFeaturesGroup from './StandardFeaturesGroup';
import { computeAccessories } from './utils';
import MathBoxOptions from './MathBoxOptions';

const PrintSummaryHeader = props => {
  const { config, pricing, params, bodyStyleTitle } = props;
  const { style, bodyType, driveType } = config;

  const year = params.modelyear;

  return (
    <div id="print-price" className="summary-print__price-info">
      <div className="price-info">
        <div className="price-info__description">
          <div className="price-info__your-carline">
            <span>
              <Translate
                value="component.price.info.carline.title"
                context={{ year, bodyStyleTitle }}
              />
            </span>
          </div>
          <div className="price-info__carline-info">
            <span>
              <Translate
                value="component.price.info.carline.info"
                context={{
                  driveType: _.get(driveType, 'id'),
                  bodyType: _.get(bodyType, 'formattedConfig'),
                  trim: _.get(style, 'trimName'),
                  year
                }}
              />
            </span>
          </div>
        </div>
        <div className="price-info__msrp-info">
          <div className="price-info__msrp-price">
            {pricing.isSeeDealer ? (
              <span className="price-info__see-dealer">
                <Translate value="unknown.price.text" />
              </span>
            ) : (
              <span className="price-info__discount-price">
                <Currency
                  value={
                    pricing.employeePrice
                      ? pricing.netEmployeePrice
                      : pricing.netPrice
                  }
                />
              </span>
            )}
          </div>
          {!pricing.isSeeDealer && (
            <div className="price-info__net-price-label">
              <Translate
                value={
                  pricing.employeePrice ? 'net.employee.price' : 'net.price'
                }
              />
            </div>
          )}
          <NotificationsContext.Consumer>
            {({ disabled }) =>
              !disabled && (
                <span className="price-info__dagger">
                  <Dagger />
                </span>
              )
            }
          </NotificationsContext.Consumer>
        </div>
      </div>
    </div>
  );
};

const PrintImage = ({ url }) =>
  url ? <img src={url} className="summary-print__vehicle-image" /> : null;

const PrintImages = props => {
  const { interiorImageUrl, exteriorImageUrl } = props;
  return (
    <div className="large-12 small-12 columns summary-print__image-container">
      <PrintImage url={exteriorImageUrl} />
      <PrintImage url={interiorImageUrl} />
    </div>
  );
};

const PrintDisclaimers = ({ pricing, disclaimers, country }) => {
  const { incentives, employeePrice } = pricing;
  const creditProgramDisclaimers = incentives.lines.map(line =>
    line.disclaimers.map((disclaimer, index) => (
      <li
        key={`${line.program}-${index}`}
        className="summary-print__disclaimer"
        dangerouslySetInnerHTML={{ __html: `${line.program} - ${disclaimer}` }}
      />
    ))
  );
  return (
    <div id="print-footer-disclaimer">
      <ul>
        {employeePrice && country === 'CA' ? (
          <React.Fragment>
            <li className="summary-print__disclaimer">
              <Translate value="byo.disclaimer.total.price" />
            </li>
            <li className="summary-print__disclaimer">
              <Translate value="byo.disclaimer.total.employee.price" />
            </li>
          </React.Fragment>
        ) : (
          <li
            className="summary-print__disclaimer"
            dangerouslySetInnerHTML={{ __html: incentives.disclaimer }}
          />
        )}
        <TranslateHTML as="li" value="accessories.price.disclaimer" />
        <li
          className="summary-print__disclaimer"
          dangerouslySetInnerHTML={{ __html: disclaimers.simple }}
        />
        <li className="summary-print__disclaimer">
          <Translate value="byo.footer.disclaimer" />
        </li>
        <li className="summary-print__disclaimer">
          <Translate value="byo.disclaimer.employee.net.price" />
        </li>
        {creditProgramDisclaimers}
      </ul>
    </div>
  );
};

const PrintSummary = props => {
  const {
    state,
    standardFeatures,
    isLoadingStandardFeatures,
    isFamilyFirstUS
  } = props;

  const {
    config,
    incentives,
    params,
    appResources,
    cgi,
    breakpoint,
    disclaimers
  } = state;
  const { style, msrp, eligibleForInvoicePrice } = config;
  const pricing = computePricing(msrp, incentives);

  const { isConvertibleTopDown, interiorIndex, exteriorIndex } = cgi;
  const images = cgiNormalize(
    appResources,
    config,
    isConvertibleTopDown,
    breakpoint
  );
  const interiorImageUrl = _.get(images.interior[interiorIndex], 'url');
  const exteriorImageUrl = _.get(images.exterior[exteriorIndex], 'url');

  return (
    <div id="print-section" className="row">
      <div className="columns">
        <PrintSummaryHeader
          bodyStyleTitle={appResources.bodyStyleTitle}
          config={config}
          pricing={pricing}
          params={params}
        />
        <PrintImages
          interiorImageUrl={interiorImageUrl}
          exteriorImageUrl={exteriorImageUrl}
        />
        <span className="summary-print__vehicle-details">
          <Translate value="vehicle.details" />
        </span>
        <div>
          <div className="summary__math-box summary__math-box--print">
            <MathBox
              pricing={pricing}
              country={params.country}
              eventName={style.vehicleInfo.promoEventName}
              isSummary={true}
              isFamilyFirstUS={isFamilyFirstUS}
              eligibleForInvoicePrice={eligibleForInvoicePrice}>
              <MathBoxOptions
                pricing={pricing}
                config={config}
                isOpenDefault={true}
              />
            </MathBox>
          </div>
        </div>
        <Accordion>
          <div className="page-break">
            <SelectedAccessoriesGroup
              accessories={computeAccessories(config)}
              isOpenDefault={true}
            />
          </div>
          <div className="summary-standard-features">
            <StandardFeaturesGroup
              standardFeatures={standardFeatures}
              loadingFeatures={isLoadingStandardFeatures}
              isOpenDefault={true}
            />
          </div>
        </Accordion>
        <PrintDisclaimers
          pricing={pricing}
          disclaimers={disclaimers}
          country={params.country}
        />
      </div>
    </div>
  );
};

export default PrintSummary;
